import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useEmblaCarousel from 'embla-carousel-react';
import {
  ArrowRight,
  CaretLeft,
  CaretRight,
  House,
  HardHat,
  Wrench,
} from '@phosphor-icons/react';
import { useWorkerLang } from '../../contexts/WorkerLanguageContext';

const SLIDES = [
  {
    id: 'homeowner',
    badge: "I'M A HOMEOWNER",
    badge_es: 'SOY PROPIETARIO',
    headline: 'Get 3 free quotes from local pros',
    headline_es: 'Reciba 3 cotizaciones gratis de profesionales locales',
    subline:
      'Compare side-by-side, schedule a site visit, and pay securely through milestone escrow.',
    subline_es:
      'Compare lado a lado, agende una visita y pague de forma segura con depósito en garantía por etapas.',
    cta: 'Get Free Quotes',
    cta_es: 'Cotizaciones Gratis',
    route: '/get-quotes',
    Icon: House,
    accent: '#2B4C6F',
    image: '/hero-homeowner.jpg',
    testid: 'carousel-slide-homeowner',
  },
  {
    id: 'contractor',
    badge: "I'M A CONTRACTOR",
    badge_es: 'SOY CONTRATISTA',
    headline: 'Win more work in your area',
    headline_es: 'Gane más trabajos en su área',
    subline:
      'Verified homeowner leads, AI pricing tools, secure payouts, and a CRM that runs itself.',
    subline_es:
      'Clientes verificados, precios con IA, pagos seguros y un CRM que trabaja solo.',
    cta: 'Join as Contractor',
    cta_es: 'Únase como Contratista',
    route: '/contractor-funnel',
    Icon: HardHat,
    accent: '#D95800',
    image: '/hero-contractor.jpg',
    testid: 'carousel-slide-contractor',
  },
  {
    id: 'worker',
    badge: "I'M A WORKER",
    badge_es: 'SOY TRABAJADOR',
    headline: 'Skilled hands wanted — every day',
    headline_es: 'Se buscan manos expertas — todos los días',
    subline:
      'Auto-match with jobs nearby, set your own rates, and get paid the moment work is done.',
    subline_es:
      'Encuentre trabajos cercanos, fije sus tarifas y reciba su pago al terminar el trabajo.',
    cta: 'Find Work',
    cta_es: 'Buscar Trabajo',
    route: '/join-as-worker',
    Icon: Wrench,
    accent: '#10B981',
    image: '/hero-worker.jpg',
    testid: 'carousel-slide-worker',
  },
];

export const AudienceCarousel = () => {
  const navigate = useNavigate();
  const { lang } = useWorkerLang();
  const es = lang === 'es';
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, duration: 25 });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const scrollTo = useCallback((idx) => emblaApi && emblaApi.scrollTo(idx), [emblaApi]);
  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    onSelect();
    return () => emblaApi.off('select', onSelect);
  }, [emblaApi]);

  // Auto-advance every 6s unless user hovers/focuses
  useEffect(() => {
    if (!emblaApi || paused) return;
    const id = setInterval(() => emblaApi.scrollNext(), 6000);
    return () => clearInterval(id);
  }, [emblaApi, paused]);

  return (
    <div
      className="relative bg-[#E8E9EB] dark:bg-[#121212] border-b-4 border-black"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      data-testid="audience-carousel"
    >
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="relative border-4 border-black shadow-[8px_8px_0px_#000] overflow-hidden">
          {/* Viewport */}
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex">
              {SLIDES.map((s) => {
                const Icon = s.Icon;
                return (
                  <div
                    key={s.id}
                    className="relative flex-[0_0_100%] min-w-0 h-[420px] md:h-[480px]"
                    data-testid={s.testid}
                  >
                    {/* Background image */}
                    <img
                      src={s.image}
                      alt={`${s.badge} on ConstructHelp`}
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                    />
                    {/* Dark scrim for legibility */}
                    <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/15" />

                    {/* Content */}
                    <div className="relative h-full max-w-3xl mx-auto md:mx-0 md:ml-12 flex flex-col justify-center px-6 md:px-10 text-white">
                      <div
                        className="inline-flex items-center gap-2 px-3 py-1.5 mb-4 w-fit border-2 border-white bg-black text-xs md:text-sm font-black uppercase tracking-widest"
                        style={{ color: s.accent }}
                      >
                        <Icon size={18} weight="bold" />
                        {es ? s.badge_es : s.badge}
                      </div>
                      <h2 className="text-3xl md:text-5xl font-black uppercase leading-[1.05] mb-3 md:mb-4 drop-shadow-[2px_2px_0_rgba(0,0,0,0.65)]">
                        {es ? s.headline_es : s.headline}
                      </h2>
                      <p className="text-base md:text-lg text-white/90 max-w-xl mb-6 md:mb-8">
                        {es ? s.subline_es : s.subline}
                      </p>
                      <button
                        onClick={() => navigate(s.route)}
                        className="h-14 px-6 w-fit flex items-center gap-2 font-black uppercase tracking-wider border-2 border-black text-white shadow-[4px_4px_0px_#000] hover:shadow-[6px_6px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all"
                        style={{ backgroundColor: s.accent }}
                        data-testid={`carousel-cta-${s.id}`}
                      >
                        {es ? s.cta_es : s.cta}
                        <ArrowRight size={22} weight="bold" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Prev / Next */}
          <button
            type="button"
            onClick={scrollPrev}
            aria-label="Previous slide"
            className="hidden md:flex absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center bg-white text-black border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[5px_5px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all"
            data-testid="carousel-prev-btn"
          >
            <CaretLeft size={22} weight="bold" />
          </button>
          <button
            type="button"
            onClick={scrollNext}
            aria-label="Next slide"
            className="hidden md:flex absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center bg-white text-black border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[5px_5px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all"
            data-testid="carousel-next-btn"
          >
            <CaretRight size={22} weight="bold" />
          </button>

          {/* Dots */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
            {SLIDES.map((s, i) => (
              <button
                key={s.id}
                type="button"
                onClick={() => scrollTo(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={`h-3 border-2 border-black transition-all ${
                  selectedIndex === i ? 'w-10 bg-white' : 'w-3 bg-white/40 hover:bg-white/70'
                }`}
                data-testid={`carousel-dot-${i}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudienceCarousel;
